import {Autocomplete, CircularProgress, TextField, Typography} from '@mui/material';
import {AlertActions} from 'presentation/components/AlertActions/index';
import Icon from 'presentation/icons/index';
import {translations} from 'presentation/translations-new';
import React, {Fragment, Suspense, lazy} from 'react';
import {useSearchProcedure} from '../hooks/useSearchProcedure';
import {SelectWrapper} from '../styles';

const AlertModal = lazy(() =>
	import('presentation/components/AlertModal/index').then(module => ({
		default: module.AlertModal,
	})),
);

const {searchProcedure} = translations['pt-br'].privateFlow.guideFlow;

export function SearchProcedure() {
	const {
		theme,
		openErrorModal,
		inputValue,
		value,
		options,
		isLoading,
		isFetchingNextPage,
		hasNextPage,
		handleCloseErrorModal,
		handleOnChange,
		handleOnInputChange,
		handleGetOptionLabel,
		handleOnHighlightChange,
		handleIsOptionEqualToValue,
		handleGoToHome,
		fetchNextPage,
		getNoOptionsText,
	} = useSearchProcedure();

	return (
		<Fragment>
			<SelectWrapper>
				<Typography variant="body2" fontWeight={600} color="text.secondary">
					{searchProcedure.searchTitle}
				</Typography>
				<Autocomplete
					popupIcon={null}
					options={options}
					value={value}
					inputValue={inputValue}
					isOptionEqualToValue={(opt, val) => handleIsOptionEqualToValue({opt, val})}
					getOptionLabel={option => handleGetOptionLabel(option)}
					onChange={(event, newValue) => handleOnChange({event, newValue})}
					onInputChange={(event, newInputValue) => {
						handleOnInputChange({event, newInputValue});
					}}
					onHighlightChange={(event, option) => {
						handleOnHighlightChange({event, option});
					}}
					slotProps={{
						listbox: {
							onScroll: (event: React.UIEvent<HTMLUListElement>) => {
								const listboxNode = event.currentTarget;
								const scrollTop = listboxNode.scrollTop;
								const scrollHeight = listboxNode.scrollHeight;
								const clientHeight = listboxNode.clientHeight;
								if (
									scrollHeight - scrollTop - clientHeight < 50 &&
									hasNextPage &&
									!isFetchingNextPage
								) {
									fetchNextPage();
								}
							},
						},
					}}
					filterOptions={x => x}
					loading={isLoading || isFetchingNextPage}
					loadingText={searchProcedure.loadingText}
					noOptionsText={getNoOptionsText()}
					renderOption={(props, option, state) => {
						const optionsLength = options.length;
						const isLastItem = state.index === optionsLength - 1;
						const handleLoadMore = () => {
							if (!isLastItem || !hasNextPage || isFetchingNextPage) return;
							fetchNextPage();
						};

						if (!option) return null;

						return (
							<li
								{...props}
								onMouseEnter={handleLoadMore}
								key={option?.cD_PROCEDIMENTO ?? state.index}>
								{option.nM_PROCEDIMENTO}
							</li>
						);
					}}
					renderInput={params => (
						<TextField
							{...params}
							placeholder={searchProcedure.searchPlaceholder}
							helperText={searchProcedure.searchHelperText}
							slotProps={{
								input: {
									...params.InputProps,
									startAdornment: (
										<Icon
											name="search-lens"
											color={theme.palette.text.disabled}
										/>
									),
									endAdornment: (
										<>
											{isLoading || isFetchingNextPage ? (
												<CircularProgress size={20} />
											) : null}
											{params.InputProps.endAdornment}
										</>
									),
								},
							}}
						/>
					)}
				/>
			</SelectWrapper>
			{openErrorModal && (
				<Suspense fallback={null}>
					<AlertModal
						open={openErrorModal}
						onClose={handleCloseErrorModal}
						variant="info"
						headerTitle={searchProcedure.modalError.headerTitle}
						title={searchProcedure.modalError.title}
						description={searchProcedure.modalError.description}
						footerContent={
							<AlertActions
								primaryAction={handleCloseErrorModal}
								primaryText={searchProcedure.modalError.primaryText}
								primaryBtnVariant="contained"
								secondaryAction={handleGoToHome}
								secondaryText={searchProcedure.modalError.secondaryText}
								secondaryBtnVariant="outlined"
								flexDirection="column"
							/>
						}
					/>
				</Suspense>
			)}
		</Fragment>
	);
}
